import {
  prisma,
  type Prisma,
  Task,
  TaskTranslations,
  TaskEntity,
  TaskEntityType,
  TaskTransition,
  TaskTransitionType,
  FormResultStatus,
  ParticipantGroupType,
} from "client";
import { transformSurveyResult } from "../formTemplates";
import { isMongoObjectId } from "@sajilni/utils/ticketQr";

// READ
export const getTask = async (): Promise<Task[]> => {
  return await prisma.task.findMany();
};

export const getTaskById = async (id: string, lang?: string) => {
  const task = await prisma.task.findUnique({
    where: { id },
    include: {
      formEntity: {
        include: {
          formTemplate: true,
        },
      },
      taskTranslations: {
        where: {
          language: {
            code: lang,
          },
        },
      },
      linkedTo: {
        include: {
          formResults: {
            include: {
              formResult: true,
            },
          },
          taskTransitions: true,
          exhibitor: {
            include: {
              translations: {
                where: {
                  language: {
                    code: lang,
                  },
                },
              },
            },
          },
        },
      },
    },
  });

  return {
    ...task,
    name: task?.taskTranslations?.[0]?.name,
    description: task?.taskTranslations?.[0]?.description,
    linkedTo: task?.linkedTo.map((item) => ({
      ...item,
      timeSubmitted:
        item.taskTransitions[item.taskTransitions.length - 1]?.createdAt ||
        null,
      status:
        item.taskTransitions[item.taskTransitions.length - 1]?.toState ||
        TaskTransitionType.NEW,
      formResult: item.formResults?.[0]?.formResult
        ? {
            ...item.formResults[0].formResult,
            surveyResult: transformSurveyResult(
              task.formEntity?.formTemplate?.json,
              item.formResults[0].formResult.surveyResult
            ),
          }
        : null,
    })),
  };
};

export const getTasksByEventId = async (
  eventId: string,
  pageNumber: number = 1,
  pageSize: number = 10,
  entityType: TaskEntityType,
  searchByName?: string,
  lang?: string,
  orderBy?: {
    [key: string]: "asc" | "desc";
  }
) => {
  const exhibitors = await prisma.exhibitor.findMany({
    where: {
      eventId,
    },
    include: {
      translations: lang
        ? {
            where: {
              language: {
                code: lang,
              },
            },
          }
        : true,
    },
  });
  const participantGroups = await prisma.participantGroup.findMany({
    where: {
      eventId,
      type:
        entityType === TaskEntityType.EXHIBITOR
          ? ParticipantGroupType.EXHIBITORS
          : ParticipantGroupType.SPONSORS,
    },
    include: {
      translations: lang
        ? {
            where: {
              language: {
                code: lang,
              },
            },
          }
        : true,
    },
  });
  const sponsors = await prisma.sponsor.findMany({
    where: {
      eventId,
    },
    include: {
      translations: lang
        ? {
            where: {
              language: {
                code: lang,
              },
            },
          }
        : true,
    },
  });
  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escapes regex special characters

  const searchParam = searchByName ? escapeRegex(searchByName) : "";
  // Calculate offset for pagination
  const skip = (pageNumber - 1) * pageSize;
  const filter = searchParam
    ? {
        OR: [
          {
            taskTranslations: {
              some: {
                OR: [
                  {
                    name: {
                      contains: searchParam,
                      mode: "insensitive" as Prisma.QueryMode,
                    },
                  },
                ],
              },
            },
          },
        ],
      }
    : {};
  try {
    // Fetch Tasks from the database
    const tasks = (await prisma.task.findMany({
      // @ts-ignore
      where: {
        eventId,
        OR: [
          {
            linkToType: {
              in: [entityType],
            },
          },
          {
            participantGroups: {
              some: {
                participantGroup: {
                  type:
                    entityType === TaskEntityType.EXHIBITOR
                      ? ParticipantGroupType.EXHIBITORS
                      : ParticipantGroupType.SPONSORS,
                },
              },
            },
          },
        ],
        ...filter,
      },
      include: {
        participantGroups: {
          include: {
            participantGroup: true,
          },
        },
        formEntity: {
          include: {
            formTemplate: true,
          },
        },
        taskTranslations: lang
          ? {
              where: {
                language: {
                  code: lang,
                },
              },
            }
          : true,
        linkedTo: {
          include: {
            taskTransitions: true,
            sponsor: {
              include: {
                translations: lang
                  ? {
                      where: {
                        language: {
                          code: lang,
                        },
                      },
                    }
                  : true,
              },
            },
            exhibitor: {
              include: {
                translations: lang
                  ? {
                      where: {
                        language: {
                          code: lang,
                        },
                      },
                    }
                  : true,
              },
            },
          },
        },
      },
      skip,
      take: pageSize,
      orderBy: orderBy || {
        createdAt: "desc", // Order by creation date or any other field
      },
    })) as any;

    // Count total Tasks for the given eventId and search filter
    const totalTasks = await prisma.task.count({
      // @ts-ignore
      where: {
        eventId,
        ...filter,
      },
    });

    // Return the data table with pagination info
    return {
      tasks: tasks.map((task: any) => {
        const completed = task.linkedTo.filter(
          (
            item: TaskEntity & {
              taskTransitions: TaskTransition[];
            }
          ) => {
            return (
              item.taskTransitions[item.taskTransitions.length - 1]?.toState ===
                TaskTransitionType.COMPLETED ||
              item.taskTransitions[item.taskTransitions.length - 1]?.toState ===
                TaskTransitionType.APPROVED ||
              item.taskTransitions[item.taskTransitions.length - 1]?.toState ===
                TaskTransitionType.REJECTED ||
              item.taskTransitions[item.taskTransitions.length - 1]?.toState ===
                TaskTransitionType.SUBMITTED
            );
          }
        );
        return {
          ...task,
          name: task.taskTranslations?.[0]?.name,
          completePercentage: {
            total: task.linkedTo.length,
            completed: completed.length,
          },
          linkedTo: task.linkedTo?.map((item: any) => ({
            ...item,
            timeSubmitted:
              item.taskTransitions[item.taskTransitions.length - 1]
                ?.createdAt || null,
            status:
              item.taskTransitions[item.taskTransitions.length - 1]?.toState ||
              TaskTransitionType.NEW,
          })),
          assignee:
            task.linkedTo.length > 0
              ? task.linkToType === TaskEntityType.EXHIBITOR
                ? task.linkedTo?.length === exhibitors.length
                  ? "All Exhibitors"
                  : task.linkedTo
                      .map((item: any) => {
                        console.log(item);
                        return item.exhibitor.translations?.[0]?.name;
                      })
                      .join(", ")
                : task.linkToType === TaskEntityType.SPONSOR
                ? task.linkedTo?.length === sponsors?.length
                  ? "All Sponsors"
                  : task.linkedTo
                      .map((item: any) => {
                        console.log(item);
                        return item.sponsor.translations?.[0]?.name;
                      })
                      .join(", ")
                : task.participantGroups?.length === participantGroups.length
                ? "All Groups"
                : participantGroups
                    ?.filter((participantGroup) =>
                      task.participantGroups?.some(
                        (t: any) => t.participantGroupId === participantGroup.id
                      )
                    )
                    ?.map((item) => item.translations?.[0]?.name)
                    .join(", ")
              : task.linkToType === TaskEntityType.SPONSOR,
        };
      }),
      pagination: {
        pageNumber,
        pageSize,
        total: totalTasks,
        totalPages: Math.ceil(totalTasks / pageSize),
        hasNextPage: totalTasks > pageNumber * pageSize,
      },
    };
  } catch (err) {
    return err;
  }
};

export const getTaskEntities = async (
  taskId: string,
  pageNumber: number = 1,
  pageSize: number = 10,
  entityType: TaskEntityType,
  searchByName?: string,
  orderBy?: {
    [key: string]: "asc" | "desc";
  },
  lang?: string
) => {
  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escapes regex special characters
  console.log("searchByName", searchByName);
  const searchParam = searchByName ? escapeRegex(searchByName) : "";
  const skip = (pageNumber - 1) * pageSize;
  const filter: any = searchParam
    ? {
        OR: [
          {
            [entityType.toLocaleLowerCase()]: {
              OR: [
                {
                  [`${entityType.toLocaleLowerCase()}Translations`]: {
                    some: {
                      name: {
                        contains: searchParam,
                        mode: "insensitive",
                      },
                    },
                  },
                },
                {
                  email: { contains: searchParam, mode: "insensitive" },
                },
                {
                  contactName: { contains: searchParam, mode: "insensitive" },
                },
                {
                  boothName: { contains: searchParam, mode: "insensitive" },
                },
                {
                  phone: { contains: searchParam, mode: "insensitive" },
                },
                {
                  contactPhone: { contains: searchParam, mode: "insensitive" },
                },
              ],
            },
          },
        ],
      }
    : {};
  try {
    const taskEntities = (await prisma.taskEntity.findMany({
      where: {
        taskId,
        ...filter,
      },
      include: {
        formResults: {
          include: {
            formResult: true,
          },
        },
        taskTransitions: true,
        task: {
          include: {
            taskTranslations: lang
              ? {
                  where: {
                    language: {
                      code: lang,
                    },
                  },
                }
              : true,
          },
        },
        [`${entityType.toLocaleLowerCase()}`]: {
          include: {
            [`${entityType.toLocaleLowerCase()}Translations`]: lang
              ? {
                  where: {
                    language: {
                      code: lang,
                    },
                  },
                }
              : {},
          },
        },
      },
      skip,
      take: pageSize,
      orderBy: orderBy || {
        createdAt: "desc", // Order by creation date or any other field
      },
    })) as any;

    const totalTaskEntities = await prisma.taskEntity.count({
      where: {
        taskId,
        ...filter,
      },
    });
    console.log("taskEntities", taskEntities);
    return {
      data: taskEntities.map((taskEntity: any) => ({
        ...taskEntity,
        [`${entityType.toLocaleLowerCase()}`]: {
          ...taskEntity[`${entityType.toLocaleLowerCase()}`],
          name: taskEntity[`${entityType.toLocaleLowerCase()}`][
            `${entityType.toLocaleLowerCase()}Translations`
          ]?.[0]?.name,
        },
        formResult: taskEntity?.formResults?.[0]?.formResult
          ? {
              ...taskEntity?.formResults[0]?.formResult,
              surveyResult: transformSurveyResult(
                taskEntity?.task?.formEntity?.formTemplate?.json,
                taskEntity?.formResults[0]?.formResult?.surveyResult
              ),
            }
          : null,
        task: {
          ...taskEntity.task,
          name: taskEntity.task.taskTranslations?.[0]?.name,
        },
        timeSubmitted:
          taskEntity.taskTransitions[taskEntity.taskTransitions.length - 1]
            ?.createdAt || null,
        status:
          taskEntity.taskTransitions[taskEntity.taskTransitions.length - 1]
            ?.toState || TaskTransitionType.NEW,
      })),
      pagination: {
        pageNumber,
        pageSize,
        total: totalTaskEntities,
        totalPages: Math.ceil(totalTaskEntities / pageSize),
        hasNextPage: totalTaskEntities > pageNumber * pageSize,
      },
    };
  } catch (err) {
    return err;
  }
};

// CREATE
export const createTask = async (
  data: Prisma.TaskCreateInput,
  eventId: string,
  translitions: TaskTranslations[],
  entities?: {
    type: TaskEntityType;
    id: string;
  }[]
) => {
  console.log(entities);
  try {
    const exhibitors =
      entities?.[0]?.type === TaskEntityType.PARTICIPANT_GROUP
        ? await prisma.exhibitor.findMany({
            where: {
              participantGroupId: {
                in: entities.map((entity) => entity.id),
              },
            },
          })
        : entities;
    const response = await prisma.task.create({
      data: {
        ...data,
        event: {
          connect: {
            id: eventId,
          },
        },
        linkToType: entities?.[0]?.type,
        linkedTo: exhibitors?.length
          ? {
              createMany: {
                data:
                  exhibitors?.map((entity) => ({
                    entityID: entity.id,
                  })) || [],
              },
            }
          : undefined,
        participantGroups:
          entities?.[0]?.type === TaskEntityType.PARTICIPANT_GROUP
            ? {
                createMany: {
                  data:
                    entities?.map((entity) => ({
                      participantGroupId: entity.id,
                    })) || [],
                },
              }
            : undefined,
        taskTranslations: {
          createMany: {
            data: translitions.map((translation) => ({
              ...translation,
            })),
          },
        },
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};

// UPDATE
export const updateTask = async (
  id: string,
  data: Prisma.TaskUpdateInput,
  translitions: TaskTranslations[],
  entities?: {
    type: TaskEntityType;
    id: string;
  }[]
) => {
  const oldTask = await prisma.task.findUnique({
    where: {
      id,
    },
  });
  const exhibitors =
    entities?.[0]?.type === TaskEntityType.PARTICIPANT_GROUP
      ? await prisma.exhibitor.findMany({
          where: {
            participantGroupId: {
              in: entities.map((entity) => entity.id),
            },
          },
        })
      : entities;
  try {
    const response = await prisma.task.update({
      where: { id },
      data: {
        ...data,
        linkToType: entities?.[0]?.type,
        linkedTo: exhibitors?.length
          ? {
              deleteMany:
                oldTask?.linkToType !== entities?.[0]?.type ? {} : undefined,

              createMany: {
                data: exhibitors.map((entity) => ({ entityID: entity.id })),
              },
            }
          : undefined,
        participantGroups:
          entities?.[0]?.type === TaskEntityType.PARTICIPANT_GROUP
            ? {
                deleteMany:
                  oldTask?.linkToType !== entities?.[0]?.type ? {} : undefined,

                createMany: {
                  data:
                    entities?.map((entity) => ({
                      participantGroupId: entity.id,
                    })) || [],
                },
                // if old task is linked to exhibitors, delete them
              }
            : undefined,
        taskTranslations: {
          deleteMany: {},
          createMany: {
            data: translitions.map((translation) => ({
              ...translation,
            })),
          },
        },
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};

export const updateTaskLinkedToTransitions = async (
  id: string,
  state: TaskTransitionType,
  comment?: string
) => {
  const taskEntity = await prisma.taskEntity.findFirst({
    where: {
      id: id,
    },
    include: {
      taskTransitions: true,
    },
  });
  if (!taskEntity) {
    throw new Error("Task Entity not found");
  }
  const lastTransition =
    taskEntity.taskTransitions?.[taskEntity.taskTransitions.length - 1];

  const response = await prisma.taskTransition.create({
    data: {
      fromState: lastTransition?.toState || TaskTransitionType.NEW,
      toState: state,
      comment,
      taskEntity: {
        connect: {
          id,
        },
      },
    },
    include: {
      taskEntity: {
        include: {
          taskTransitions: true,
          task: {
            include: {
              createdBy: true,
              taskTranslations: true,
              event: {
                include: {
                  translations: true,
                  whiteLabel: true,
                },
              },
            },
          },
          exhibitor: true,
        },
      },
    },
  });
  console.log(response);
  return response;
};

// DELETE
export const deleteTask = async (id: string): Promise<Task> => {
  try {
    await prisma.taskEntity.deleteMany({
      where: {
        taskId: id,
      },
    });
    await prisma.taskTransition.deleteMany({
      where: {
        taskEntity: {
          taskId: id,
        },
      },
    });
    await prisma.taskTranslations.deleteMany({
      where: {
        taskId: id,
      },
    });
    await prisma.participantGroupTask.deleteMany({
      where: {
        taskId: id,
      },
    });
    await prisma.formEntity.delete({
      where: {
        entityID: id,
      },
    });
    return await prisma.task.delete({
      where: { id },
    });
  } catch (err: any) {
    throw new Error(err.message);
  }
};

export const updateTaskEntityWithUploads = async (
  id: string,
  uploads: {
    url: string;
    key: string;
  }[],
  status?: FormResultStatus
) => {
  const response = await prisma.taskEntity.update({
    where: {
      id,
    },
    data: {
      uploadedFiles: uploads,
      uploadedFilesStatus: status || FormResultStatus.COMPLETED,
    },
  });
  return response;
};

const visibleTaskEntities = (lang?: string) =>
  ({
    where: {
      task: {
        OR: [
          { hide: false },
          { AND: [{ hide: true, dueDate: { lt: new Date() } }] },
        ],
      },
    },
    select: {
      id: true,
      uploadedFiles: true,
      taskTransitions: {
        orderBy: { createdAt: "desc" as const },
        take: 1,
        select: { toState: true },
      },
      formResults: {
        take: 1,
        select: { formResult: true },
      },
      task: {
        select: {
          dueDate: true,
          type: true,
          requireApproval: true,
          disableAfterDue: true,
          uploadFiles: true,
          formEntity: {
            select: { formTemplate: { select: { id: true, json: true } } },
          },
          taskTranslations: {
            ...(lang ? { where: { language: { code: lang } } } : {}),
            select: { name: true },
            take: 1,
          },
        },
      },
    },
  });

type PortalTaskEntityRow = {
  id: string;
  uploadedFiles?: unknown;
  taskTransitions: { toState: TaskTransitionType }[];
  formResults: Array<{
    formResult?: { surveyResult?: unknown } | null;
  }>;
  task: {
    dueDate: Date;
    disableAfterDue?: boolean | null;
    formEntity?: { formTemplate?: { json?: unknown } | null } | null;
  } | null;
};

const mapPortalTaskEntities = (taskEntities: PortalTaskEntityRow[]) =>
  taskEntities.map((taskEntity) => ({
    ...taskEntity,
    stopAcceptingSubmissions:
      !!taskEntity.task &&
      taskEntity.task.dueDate < new Date() &&
      taskEntity.task.disableAfterDue === true,
    formResult: taskEntity.formResults?.[0]?.formResult
      ? {
          ...taskEntity.formResults[0].formResult,
          surveyResult: transformSurveyResult(
            taskEntity.task?.formEntity?.formTemplate?.json,
            taskEntity.formResults[0].formResult?.surveyResult
          ),
        }
      : null,
  }));

/** Indexed: Exhibitor/Sponsor @@index([eventId, teamIds]). Tasks via TaskEntity @@index([entityID]). */
export const getParticipantTasksForPortal = async (
  slug: string,
  userId: string,
  eventId: string,
  type: ParticipantGroupType,
  lang?: string
) => {
  const where = isMongoObjectId(eventId)
    ? { eventId, teamIds: { has: userId } }
    : { event: { slug }, teamIds: { has: userId } };
  const taskEntities = visibleTaskEntities(lang);
  const translationSelect = {
    select: { name: true, language: { select: { code: true } } },
  } as const;
  const settingsPromise =
    type === ParticipantGroupType.EXHIBITORS
      ? prisma.exhibitorSettings.findUnique({
          where: { eventId },
          select: { supportEmail: true },
        })
      : type === ParticipantGroupType.SPONSORS
        ? prisma.sponsorSettings.findUnique({
            where: { eventId },
            select: { supportEmail: true },
          })
        : Promise.resolve(null);

  const companyPromise =
    type === ParticipantGroupType.EXHIBITORS
      ? prisma.exhibitor.findFirst({
          where,
          select: {
            id: true,
            translations: translationSelect,
            taskEntities,
          },
        })
      : type === ParticipantGroupType.SPONSORS
        ? prisma.sponsor.findFirst({
            where,
            select: {
              id: true,
              translations: translationSelect,
              taskEntities,
            },
          })
        : Promise.resolve(null);

  const [res, settings] = await Promise.all([companyPromise, settingsPromise]);
  if (!res) return null;

  const langData =
    res.translations.find((item) => item.language.code === lang) ??
    res.translations[0];
  return {
    id: res.id,
    name: langData?.name,
    translations: res.translations,
    supportEmail: settings?.supportEmail,
    taskEntities: mapPortalTaskEntities(
      res.taskEntities as PortalTaskEntityRow[]
    ),
  };
};
